import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shopsService } from '../services/shops.service';
import type { ImageObject } from '@/lib/types';

type ImageInput = string | ImageObject | null | undefined;

export interface ImageOrderItem {
  url: string;
  order: number;
  isFile: boolean;
  name: string;
  id: string;
}

/**
 * Normalizes filename to avoid encoding issues between client and server
 */
function normalizeFilename(name: string): string {
  const ext = name.split('.').pop() || '';
  const baseName = name.slice(0, -(ext.length + 1));
  const normalized = baseName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-zA-Z0-9_-]/g, '_') // Replace special chars with underscore
    .replace(/_+/g, '_') // Collapse multiple underscores
    .slice(0, 50); // Limit length
  return `${normalized}.${ext}`;
}

/**
 * Transforms images to the format expected by the server
 */
export function transformImagesToOrders(
  existingImages: ImageInput[],
  imagesToRemove: number[],
  newImages: File[]
): { orders: ImageOrderItem[]; renamedImages: File[] } {
  const orders: ImageOrderItem[] = [];
  let maxOrder = -1;

  // Add existing images that are NOT being removed
  existingImages.forEach((img, index) => {
    if (!imagesToRemove.includes(index)) {
      // Handle both string URLs and ImageObject
      const imgObj = typeof img === 'object' && img !== null ? img : null;
      const imageUrl = imgObj?.url || (typeof img === 'string' ? img : '');

      // Skip images without valid URL
      if (!imageUrl || !imageUrl.startsWith('http')) {
        console.warn(`[transformImagesToOrders] Skipping image at index ${index} - invalid URL:`, imageUrl);
        return;
      }

      const imageOrder = Number(imgObj?.order ?? index);
      const imageName = imageUrl.split('/').pop() || `image-${index}`;

      orders.push({
        url: imageUrl,
        order: imageOrder,
        isFile: false,
        name: imageName,
        id: imgObj?.id ? String(imgObj.id) : String(index),
      });

      if (imageOrder > maxOrder) maxOrder = imageOrder;
    }
  });

  // Rename files to normalized names for consistent server matching
  const renamedImages = newImages.map((file) => {
    const normalizedName = normalizeFilename(file.name);
    return new File([file], normalizedName, { type: file.type });
  });

  // Add new images as file placeholders
  renamedImages.forEach((file, idx) => {
    const newOrder = maxOrder + 1 + idx;
    orders.push({
      url: 'file',
      order: newOrder,
      isFile: true,
      name: file.name,
      id: `new-${newOrder}`,
    });
  });

  return { orders, renamedImages };
}

/**
 * Validates image orders match server requirements
 */
export function validateImageOrders(orders: ImageOrderItem[]): { valid: boolean; error?: string } {
  // Server requires 1-2 images for shops
  if (orders.length === 0 || orders.length > 2) {
    return { valid: false, error: `Invalid image count: ${orders.length}. Shop requires 1-2 images.` };
  }

  const urlPattern = /^(http|https):\/\/[^\s$.?#].[^\s]*$/;

  for (const order of orders) {
    const isValidUrl = typeof order.url === 'string' && (urlPattern.test(order.url) || order.url === 'file');
    const isValidOrder = typeof order.order === 'number' && order.order >= 0;
    const isValidFileFlag = typeof order.isFile === 'boolean';
    const isValidId = typeof order.id === 'string' || typeof order.id === 'number';
    const isValidName = typeof order.name === 'string' && order.name.trim().length > 0;

    if (!isValidUrl || !isValidOrder || !isValidFileFlag || !isValidId || !isValidName) {
      return {
        valid: false,
        error: `Invalid image data: ${JSON.stringify({ order, validation: { isValidUrl, isValidOrder, isValidFileFlag, isValidId, isValidName } })}`,
      };
    }
  }

  return { valid: true };
}

interface UpdateShopImagesInput {
  shopId: string;
  existingImages: ImageInput[];
  imagesToRemove: number[];
  newImages: File[];
  /** Optional custom orders - if provided, overrides the default transformation */
  customOrders?: ImageOrderItem[];
}

export function useUpdateShopImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shopId, existingImages, imagesToRemove, newImages, customOrders }: UpdateShopImagesInput) => {
      let orders: ImageOrderItem[];
      let renamedImages: File[];

      if (customOrders) {
        // Use custom orders (for reordering support)
        orders = customOrders;
        // Rename new image files for consistency
        renamedImages = newImages.map((file) => {
          const normalizedName = normalizeFilename(file.name);
          return new File([file], normalizedName, { type: file.type });
        });
        // Update custom orders with renamed file names
        let newImageIdx = 0;
        orders = orders.map(order => {
          if (order.isFile && renamedImages[newImageIdx]) {
            const renamed = { ...order, name: renamedImages[newImageIdx].name };
            newImageIdx++;
            return renamed;
          }
          return order;
        });
      } else {
        // Use default transformation
        const result = transformImagesToOrders(existingImages, imagesToRemove, newImages);
        orders = result.orders;
        renamedImages = result.renamedImages;
      }

      const validation = validateImageOrders(orders);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      return shopsService.updateShopImages(shopId, orders, renamedImages);
    },
    onSuccess: (_, { shopId }) => {
      queryClient.invalidateQueries({ queryKey: ['shops'] });
      queryClient.invalidateQueries({ queryKey: ['shops', shopId] });
    },
  });
}
